import { consola } from 'consola'
import { ref, computed } from 'vue'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { getPlatformName, isNativePlatform, ensureVkIdSdk } from '@shared/lib'
import { getClientFingerprint } from '@shared/lib/auth'
import { useModalStore } from '@shared/model'
import apiAuth, { useSessionManager } from '~/entities/auth'
import { useLogoutHandler } from '~/features/auth/model'
import { VkIdAuth, isVkIdNativeAvailable } from '~/features/auth/lib/vkIdNativePlugin'
import { useAuthAnalytics } from '~/features/auth/model/useAuthAnalytics'

const logger = consola.withTag('VkAuth')

// Дефолты на случай, если конфиг заведения ещё не подъехал (мультибренд-приложение VK ID).
const DEFAULT_VK_APP_ID = 54_661_200
const DEFAULT_VK_REDIRECT_URL = 'https://xn--80akufmr7a.store/api/v2/auth/vk/callback'

// Права, запрашиваемые у VK ID. `phone` — доступ к номеру телефона (выдаётся
// приложению после модерации в кабинете VK ID); без него VK телефон не вернёт.
const VK_AUTH_SCOPE = 'phone'

type VkPlatform = 'web' | 'android' | 'ios'

// Таймаут загрузки SDK живёт в loadScriptOnce — опрашивать window здесь больше нечего.

/**
 * Авторизация через VK ID (клиентский сценарий SDK @vkid/sdk).
 *
 * Поток (в отличие от Telegram/MAX здесь НЕТ поллинга — весь обмен в браузере):
 *   1. Рендерим виджет VKID.OneTap в переданный контейнер.
 *   2. По событию LOGIN_SUCCESS сам SDK меняет `code`+`device_id` на токены
 *      (`VKID.Auth.exchangeCode`, PKCE внутри SDK).
 *   3. Полученные access_token/id_token отправляем на бэк `POST /auth/vk/exchange`.
 *   4. Бэк независимо проверяет токен у VK, находит/создаёт юзера и выдаёт наши JWT.
 *   5. Сохраняем сессию, грузим профиль, закрываем модалку.
 *
 * Кнопку показываем только если у заведения настроена активная интеграция VK (`vk_auth_enabled`).
 */
export const useVkAuth = () => {
  const storeInstitution = useInstitutionStore()
  const sessionManager = useSessionManager()
  const userProfileStore = useUserProfileStore()
  const logoutHandler = useLogoutHandler()
  const authAnalytics = useAuthAnalytics()
  const modalStore = useModalStore()

  const isLoading = ref(false)
  const errorMessage = ref<string | null>(null)

  let rendered = false

  const isVkAuthEnabled = computed(() => storeInstitution.institution?.vk_auth_enabled === true)

  /** Текущая платформа клиента (web в SSR/браузере, ios/android в Capacitor). */
  const currentPlatform = (): VkPlatform => getPlatformName() as VkPlatform

  /**
   * Выбирает app_id/redirect_url приложения VK ID под текущую платформу.
   * Пустой слот платформы откатывается на web-слот, затем на дефолт бренда.
   */
  const resolveVkCredentials = (): { appId: number; redirectUrl: string } => {
    const inst = storeInstitution.institution as any
    const platform = currentPlatform()

    let appIdRaw: string | null | undefined
    let redirectRaw: string | null | undefined
    if (platform === 'android') {
      appIdRaw = inst?.vk_app_id_android ?? inst?.vk_app_id
      redirectRaw = inst?.vk_redirect_url_android ?? inst?.vk_redirect_url
    } else if (platform === 'ios') {
      appIdRaw = inst?.vk_app_id_ios ?? inst?.vk_app_id
      redirectRaw = inst?.vk_redirect_url_ios ?? inst?.vk_redirect_url
    } else {
      appIdRaw = inst?.vk_app_id
      redirectRaw = inst?.vk_redirect_url
    }

    return {
      appId: Number(appIdRaw) || DEFAULT_VK_APP_ID,
      redirectUrl: redirectRaw || DEFAULT_VK_REDIRECT_URL,
    }
  }

  /**
   * Отдаёт VK ID SDK, подгрузив его при первом обращении.
   *
   * Раньше UMD-скрипт висел тегом в <head> обоих лейаутов и грузился на каждой
   * странице сайта, а здесь оставалось только дождаться его опросом. Теперь он
   * приезжает ровно тогда, когда рисуется виджет входа.
   */
  const waitForSdk = (): Promise<any> => ensureVkIdSdk()

  const handleError = (error: unknown) => {
    // Логируем сырой объект ошибки VK — по нему видно, почему виджет не отрисовался
    // (частая причина на dev — origin не в списке доверенных доменов приложения VK ID).
    logger.error('Ошибка VK ID авторизации (виджет не отрисован / ошибка входа):', error)
    errorMessage.value = 'Не удалось войти через VK ID. Попробуйте ещё раз.'
    isLoading.value = false
  }

  /** Отправляем полученные от SDK токены на бэк и логиним пользователя. */
  const exchangeOnBackend = async (payload: any) => {
    try {
      isLoading.value = true
      errorMessage.value = null

      // В вебе клик по OneTap до нас не долетает — виджет рисует SDK внутри своего
      // контейнера. Первый момент, когда мы вообще узнаём о попытке входа, — вот этот.
      // На нативе попытка уже отмечена кнопкой, повтор просто продлевает её.
      authAnalytics.trackMethodClick('vk')

      const response = await apiAuth.vkExchange({
        institutionId: String(storeInstitution.institution?.id),
        accessToken: payload?.access_token,
        idToken: payload?.id_token,
        userId: payload?.user_id == null ? undefined : String(payload.user_id),
        deviceId: payload?.device_id,
        clientFingerprint: await getClientFingerprint(),
        // Бэк по platform выбирает правильный app_id (client_id) для проверки токена.
        platform: currentPlatform(),
      })

      const data = response.data
      if (data?.status !== 'completed' || !data?.accessToken) {
        authAnalytics.trackCallback('vk', false, data?.status || 'exchange_failed')
        logger.error('VK ID: неуспешный ответ бэкенда:', data)
        errorMessage.value = data?.message || 'Ошибка авторизации через VK ID'
        isLoading.value = false
        return
      }

      authAnalytics.trackCallback('vk', true)
      await sessionManager.setSession(data.accessToken, data.sessionId, data.refreshToken)
      logoutHandler.handleSuccessfulLogin()
      await userProfileStore.fetchUser()

      if (process.client) {
        try {
          const { useApplyReferralToken } = await import('~/features/profile/@x/auth')
          await useApplyReferralToken()
        } catch (error) {
          logger.warn('Реферальный токен не применён (не критично):', error)
        }
      }

      if (process.client) {
        try {
          const pushModule = await import('~/features/notifications/@x/auth')
          await pushModule.usePushNotificationSettings().initPushNotificationsAfterAuth()
        } catch (error) {
          logger.warn('Push notifications не инициализированы (не критично):', error)
        }
      }

      logger.success('VK ID авторизация успешна')
      modalStore.close()
      isLoading.value = false
    } catch (error: any) {
      logger.error('Ошибка обмена токенов VK ID:', error)
      errorMessage.value = error?.data?.message || 'Ошибка авторизации через VK ID'
      isLoading.value = false
    }
  }

  /**
   * Инициализирует и рендерит виджет VKID.OneTap в переданный контейнер.
   * Вызывать из onMounted компонента (только на клиенте) когда `isVkAuthEnabled`.
   */
  const renderOneTap = async (container: HTMLElement) => {
    // На нативных платформах виджет не рендерим — вход идёт через нативный VKID SDK
    // (см. loginNative). Веб-виджет ненадёжен внутри WebView (PKCE в cookie).
    if (isNativePlatform()) return
    if (rendered || !isVkAuthEnabled.value || typeof window === 'undefined') return
    // Ставим флаг оптимистично, чтобы конкурентные вызовы (onMounted + watcher) не
    // инициализировали виджет дважды; при ошибке — откатываем, чтобы можно было повторить.
    rendered = true

    try {
      const VKID = await waitForSdk()
      const { appId, redirectUrl } = resolveVkCredentials()
      logger.debug('VK ID SDK загружен, рендерим OneTap', {
        appId,
        platform: currentPlatform(),
        origin: window.location.origin,
      })

      VKID.Config.init({
        app: appId,
        redirectUrl,
        responseMode: VKID.ConfigResponseMode.Callback,
        source: VKID.ConfigSource.LOWCODE,
        // Запрашиваем номер телефона (право выдано приложению в кабинете VK ID).
        // VK вернёт phone в oauth2/user_info → бэк сохранит его пользователю.
        scope: VK_AUTH_SCOPE,
      })

      const oneTap = new VKID.OneTap()

      oneTap
        .render({
          container,
          showAlternativeLogin: true,
          // Виджет рисует VK, и по умолчанию он выше и прямоугольнее остальных кнопок
          // входа. Приводим к общему виду: 48px и pill, как у Telegram, MAX, Яндекса
          // и главной кнопки формы.
          styles: {
            borderRadius: 24,
            height: 48,
          },
        })
        .on(VKID.WidgetEvents.ERROR, handleError)
        .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload: any) => {
          const code = payload.code
          const deviceId = payload.device_id
          VKID.Auth.exchangeCode(code, deviceId).then(exchangeOnBackend).catch(handleError)
        })

    } catch (error) {
      rendered = false
      handleError(error)
    }
  }

  /**
   * Вход через нативный VKID SDK (iOS/Android). Вызывать по нажатию на VK-кнопку
   * на нативных платформах — там нет веб-виджета OneTap.
   */
  const loginNative = async () => {
    if (!isVkAuthEnabled.value) return
    if (!isVkIdNativeAvailable()) {
      logger.warn('Нативный VKID SDK недоступен на этой платформе/сборке')
      errorMessage.value = 'Вход через VK ID недоступен в этом приложении'
      return
    }

    try {
      isLoading.value = true
      errorMessage.value = null

      const { appId } = resolveVkCredentials()
      const result = await VkIdAuth.authorize({ appId: String(appId), scope: VK_AUTH_SCOPE })

      // Приводим к форме payload'а веб-SDK, чтобы переиспользовать exchangeOnBackend.
      await exchangeOnBackend({
        access_token: result.accessToken,
        id_token: result.idToken,
        user_id: result.userId,
        device_id: result.deviceId,
      })
    } catch (error) {
      handleError(error)
    }
  }

  const cleanup = () => {
    rendered = false
    isLoading.value = false
    errorMessage.value = null
  }

  return {
    renderOneTap,
    loginNative,
    cleanup,
    isLoading,
    errorMessage,
    isVkAuthEnabled,
    isNativePlatform: computed(() => isNativePlatform()),
  }
}
